import { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Message } from '@arco-design/web-react';

import { ConfigContext } from '@/components/ConfigProvider';
import { isValidClusterName } from '@/utils/tenantClusterStorage';
import { _getStorage } from '@/utils/useLocalStorage';

const getval = (data, defaultVal) => {
  if (!data?.length) return { _tenant: defaultVal };
  return { _tenant: data?.[0]?.name || ':tenant' };
};

// 默认租户及租户下的集群
function useTenants() {
  const { t } = useTranslation();
  const params = useParams();
  const routeTenant = params?.tenant;
  const routeCluster = params?.cluster;
  const { tenantList, tenant, setTenant, tenantCluster, setTenantCluster, tenantClusters } = useContext(ConfigContext);

  const setTenant1 = useCallback(
    (v) => {
      if (typeof setTenant !== 'function' || !v || v === 'undefined' || v === ':tenant') return;
      setTenant(v);
      localStorage.setItem('tenant', JSON.stringify(v));
    },
    [setTenant],
  );

  const setCluster = useCallback(
    (v) => {
      if (!isValidClusterName(v) || typeof setTenantCluster !== 'function') return;
      setTenantCluster(v, tenant);
    },
    [setTenantCluster, tenant],
  );

  // 只用路由参数的原始值作依赖：useParams() 每次 render 都是新对象，不能直接依赖 params。
  useEffect(() => {
    if (routeTenant) {
      setTenant1(routeTenant);
    }
    // 无 cluster / cluster 为 "undefined" 时不要覆盖已有数据中心，否则会与下方回退逻辑死循环。
    if (routeTenant && isValidClusterName(routeCluster) && routeCluster !== tenantCluster) {
      setTenantCluster?.(routeCluster, routeTenant);
    }
  }, [routeTenant, routeCluster, tenantCluster, setTenant1, setTenantCluster]);

  useEffect(() => {
    if (window.location.pathname.startsWith('/console')) {
      const arr = window.location.pathname?.split('/');
      const tenantName = arr[2];
      if (tenantName && tenantList?.length) {
        const curr = tenantList?.find((i) => i.name === tenantName);
        if (!curr) {
          Message.error(t('hooks:the.current.tenant.does.not.exist'));
        }
      }
    }
  }, [tenantList, t]);

  useEffect(() => {
    if (!tenant || tenant === 'undefined' || tenant === ':tenant') {
      const { _tenant } = getval(tenantList, _getStorage('tenant', ''));
      setTenant1(_tenant);
    }
  }, [tenantList, tenant, setTenant1]);

  useEffect(() => {
    if (isValidClusterName(tenantCluster)) return;
    const nextCluster = tenantClusters?.[0]?.name;
    if (isValidClusterName(nextCluster) && nextCluster !== tenantCluster) {
      setCluster(nextCluster);
    }
  }, [tenantClusters, tenantCluster, setCluster]);

  return {
    tenant,
    tenantList,
    setTenant: setTenant1,
    getCurrTentant: (n) => tenantList.find((i) => i.name === n),
    cluster: tenantCluster,
    clusterList: tenantClusters,
    setCluster,
  };
}

export default useTenants;
